import React, { useMemo, useRef } from 'react';
import { Typography } from 'fronton-react';
import { BookOpenIcon, SignpostIcon } from '@fronton/icons-react';
import { LinkWithGAPromoClick, Section, Swiper, SwiperSlide } from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey, removeSpecialCharacters } from 'utils';
import {
  adviceCard,
  adviceCardBody,
  adviceCardImage,
  adviceCardImageWrapper,
  adviceCardTopic,
  adviceTitle,
  adviceSlide,
  adviceCardInfo,
  adviceCardInfoItem,
  adviceCardTitle,
} from './advices-styles';

type AdvicesProps = {
  elementBlock: string;
  title: string;
  advices: {
    image: string;
    title: string;
    href: string;
    topic: {
      icon: React.ReactElement;
      name: string;
    };
    info: {
      date: string;
      type: string;
      duration: string;
    };
  }[];
};

/**
 * Вью секции с советами
 */
export const Advices: React.FC<AdvicesProps> = ({ advices, elementBlock, title }) => {
  const bannerRefs = useRef<HTMLAnchorElement[]>([]);

  const promotions = useMemo(() =>advices.map((advice, index) => ({
    name: `${elementBlock}_советы`,
    creative: removeSpecialCharacters(advice.title),
    position: (index + 1).toString(),
  })), [advices, elementBlock]);

  usePromoViewEvent(bannerRefs, `${elementBlock} / articles`, promotions);

  return (
    <Section>
      <Typography className={adviceTitle} variant="h2" size="extra" as="h2">
        {title}
      </Typography>

      <Swiper slidesPerView="auto">
        {advices.map(({ title: cardTitle, topic, info, image, href }, index) => (
          <SwiperSlide className={adviceSlide} key={createElementKey('advice', index)}>
            <LinkWithGAPromoClick
              bannerRefs={bannerRefs}
              className={adviceCard}
              elementBlock={elementBlock}
              position={index}
              promotion={promotions[index]}
              href={href}
              title={cardTitle}
            >
              <div className={adviceCardImageWrapper}>
                <img
                  alt={'creative'}
                  className={adviceCardImage}
                  data-position={index}
                  height={190}
                  src={image}
                  width={380}
                />
              </div>
              <div className={adviceCardBody}>
                <div className={adviceCardTopic}>
                  {topic.icon}
                  <Typography variant="mini" size="overline">{topic.name}</Typography>
                </div>
                <Typography className={adviceCardTitle} variant="m" size="subtitle">
                  {cardTitle}
                </Typography>
                <div className={adviceCardInfo}>
                  <Typography variant="minor" size="caption">{info.date}</Typography>
                  <div className={adviceCardInfoItem}>
                    <SignpostIcon type='outline' size='s' color='text-secondary' />
                    <Typography variant="minor" size="caption">Гайд</Typography>
                  </div>
                  <div className={adviceCardInfoItem}>
                    <BookOpenIcon type='outline' size='s' color='text-secondary' />
                    <Typography variant="minor" size="caption">{info.duration}</Typography>
                  </div>
                </div>
              </div>
            </LinkWithGAPromoClick>
          </SwiperSlide>
        ))}
      </Swiper>
    </Section>
  );
};
